import { AudioSimplePlayer } from "@/component/AudioSimplePlayer";
import { ProgressiveImage } from "@/component/ProgressiveImage";
import { VideoPreview } from "@/component/VideoPreview";
import { Attachment } from "@/types/attachment";
import { inferMediaKind } from "@/util/file";
import { CloseOutlined, FileOutlined } from "@ant-design/icons";
import styles from "./index.module.css";

type ChatBoxFileSize = "small" | "large";

interface IProps {
  attachmentList: Attachment[];
  onRemove?: (file: Attachment) => void;
  size?: ChatBoxFileSize; // small: 48px radius 8; large: 80px radius 16
  disabled?: boolean;
}

export function ChatBoxFiles(props: IProps) {
  const { attachmentList, onRemove, size = "large", disabled = false } = props;
  if (attachmentList.length === 0) {
    return null;
  }

  const isSmall = size === "small";
  const itemSize = isSmall ? 48 : 80;
  const borderRadius = isSmall ? 8 : 16;
  const closeButtonSize = isSmall ? 20 : 28;

  return (
    <div className="flex gap-3 flex-wrap">
      {attachmentList.map((item, index) => {
        const mediaKind = inferMediaKind(item);
        const isVideo = mediaKind === "video";
        const isImage = mediaKind === "image";
        const isAudio = mediaKind === "audio";
        const previewSrc =
          item.type === "file" ? item.preview : item.preview || item.url;

        if (!previewSrc) {
          return null;
        }

        return (
          <div
            key={item.id}
            className={`${styles.fileItem}`}
            style={{
              width: `${itemSize}px`,
              height: `${itemSize}px`,
              borderRadius: `${borderRadius}px`,
            }}
          >
            {isVideo && (
              <VideoPreview
                src={previewSrc}
                width={itemSize}
                height={itemSize}
              />
            )}
            {isImage && (
              <ProgressiveImage
                src={previewSrc}
                alt={`File ${index + 1}`}
                containerClassName="w-full h-full"
                className="w-full h-full object-cover cursor-pointer"
                width={itemSize}
                height={itemSize}
              />
            )}
            {isAudio && (
              <div
                style={{
                  width: `${itemSize}px`,
                  height: `${itemSize}px`,
                }}
              >
                <AudioSimplePlayer src={previewSrc} />
              </div>
            )}
            {!isVideo && !isImage && !isAudio && (
              <div
                className="flex items-center justify-center"
                style={{
                  width: `${itemSize}px`,
                  height: `${itemSize}px`,
                }}
              >
                <FileOutlined />
              </div>
            )}
            {!disabled && (
              <div
                className={styles.closeButtonWrapper}
                style={{
                  width: `${closeButtonSize}px`,
                  height: `${closeButtonSize}px`,
                }}
              >
                <button
                  type="button"
                  onClick={() => onRemove?.(item)}
                  className="cursor-pointer"
                >
                  <CloseOutlined
                    style={{
                      width: `${closeButtonSize / 2}px`,
                      height: `${closeButtonSize / 2}px`,
                    }}
                  />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
